import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Section } from "@/components/section";
import { StateBadge } from "@/components/badge/state-badge";
import { defaultStateFlow } from "@/constants/version-state";
import { DEFAULT_CHANNEL_RELEASABLE_STATES } from "@/components/badge/channel-state";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";

const norm = (codes: number[]) => [...new Set(codes)].sort((a, b) => a - b);

// 渠道发版门槛:版本走到 version flow 的哪些节点后,渠道才能对它记录发布
// (产品推版本,运维/运营从这些节点接手做各渠道的放量回收)。勾选范围限本 app
// 启用的版本状态;全不勾存 null = 回到默认(待发版/审核中/放量中/线上)。
// 门槛在服务端写路径强制,这里只是配置入口。
export const ChannelGateSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const flow = app.versionStateFlow ?? defaultStateFlow();
  const enabledStates = Object.keys(flow)
    .filter((k) => k !== "start")
    .map(Number)
    .sort((a, b) => a - b);

  // 与 version flow 求交集:flow 重配后残留的旧状态码不再显示、保存即剔除
  const saved = norm(
    (app.channelReleasableStates ?? DEFAULT_CHANNEL_RELEASABLE_STATES).filter(
      (c) => enabledStates.includes(c)
    )
  );
  const [selected, setSelected] = useState<number[]>(saved);
  const dirty = norm(selected).join(",") !== saved.join(",");

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/channel-gate`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        toast({ title: "渠道发版门槛已保存" });
      },
      onError: (err: unknown) => toastError(toast, "保存失败", err),
    }
  );

  const toggle = (code: number, on: boolean) =>
    setSelected((prev) =>
      on ? [...prev, code] : prev.filter((c) => c !== code)
    );

  return (
    <Section
      title="渠道发版门槛"
      action={
        <Button
          size="sm"
          className="h-8"
          disabled={!dirty || isMutating}
          onClick={() => trigger({ states: norm(selected) })}
        >
          {isMutating ? "保存中…" : "保存"}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {enabledStates.map((code) => (
          <label key={code} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={selected.includes(code)}
              onCheckedChange={(v) => toggle(code, v === true)}
            />
            <StateBadge state={code} />
          </label>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        版本处于勾选状态时,渠道才能对它记录发布(打包放量)。全不勾 =
        回到默认:待发版/审核中/放量中/线上。
      </p>
    </Section>
  );
};

export default ChannelGateSection;
